import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextareaAutosize,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { inputStyleObj } from "./WriteOff";
import { acctMonthToLabel, useCfi, getTodaysDateInLocalTimezone } from "./hooks";
import SettingsTwoToneIcon from "@mui/icons-material/SettingsTwoTone";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UpdateGenericWorkflowDialog from "../Workflow/UpdateGenericWorkflowDialog";
import { customDateFormat } from "./utils";
import { numberFormatter } from "../../utils/generalFunctions";
import { toast, ToastContainer } from "react-toastify";

const History = ({ fixedDetails, history }) => {
  const [
    openUpdateGenericCfiWorkflowDialog,
    setOpenUpdateGenericCfiWorkflowDialog,
  ] = useState(false);
  const [currentWorkflowName, setCurrentWorkflowName] = useState("");
  const [currentTxnWorkflowDetails, setCurrentTxnWorkflowDetails] = useState(
    {}
  );
  const handleUpdateGenericWorkflowDialog = () => {
    setOpenUpdateGenericCfiWorkflowDialog(false);
  };
  const hasProceededWorkflow = (workflow) => {
    const status = workflow?.workflow_status?.trim()?.toLowerCase();
    if (!workflow?.workflow_json_data?.workflow_name || !status) return false;

    const nonProceededStatuses = new Set([
      "not started",
      "not initiated",
      "initiated",
      "pending",
      "draft",
    ]);
    return !nonProceededStatuses.has(status);
  };

  return (
    <div className="cash-allocation-dialogue-container">
      <Grid container>
        {fixedDetails.map(({ label, value }, i) => (
          <Grid
            item
            xs={12}
            md={6}
            key={label}
            paddingLeft={i % 2 ? 2 : 0}
            paddingRight={i % 2 ? 0 : 2}
            marginBottom={2}
          >
            <span style={{ fontWeight: label === "Policy#" ? 800 : 400 }}>
              {label}
            </span>
            <span style={{ fontWeight: "bold" }}>: {value}</span>
          </Grid>
        ))}
      </Grid>
      <TableContainer sx={{ height: 275, overflow: "auto" }}>
        <Table>
          <TableHead className="table-head">
            <TableRow>
              <TableCell
                className="table-cell p-0"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                New Allocation Date
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Changes
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Impact
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Amt
              </TableCell>
              <TableCell
                className="table-cell p-0"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Ccy
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Date Process in system
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                WF Status
              </TableCell>
              <TableCell
                className="table-cell"
                sx={{ fontSize: "15px", fontWeight: 800 }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="table-body">
            {history.map((row, index) => (
              <TableRow key={index} className="table-row">
                <TableCell className="table-cell">
                  {customDateFormat(row?.request_date)}
                </TableCell>
                <TableCell className="table-cell">{row?.cfi_change}</TableCell>
                <TableCell className="table-cell">{row?.cfi_impact}</TableCell>
                <TableCell className="table-cell">{numberFormatter(row?.cfi_amt ?? 0)}</TableCell>
                <TableCell className="table-cell">{row?.cfi_curr}</TableCell>
                <TableCell className="table-cell">
                  {row?.process_date && customDateFormat(row?.process_date)}
                </TableCell>
                <TableCell className="table-cell">
                  {row?.workflow?.workflow_status}
                </TableCell>
                <TableCell className="table-cell">
                  <IconButton
                    sx={{ visibility: hasProceededWorkflow(row?.workflow) ? "visible" : "hidden" }}
                  >
                    <SettingsTwoToneIcon
                      onClick={() => {
                        if (!hasProceededWorkflow(row?.workflow)) return;
                        setCurrentWorkflowName(
                          row?.workflow?.workflow_json_data?.workflow_name ?? ""
                        );
                        setCurrentTxnWorkflowDetails(row?.workflow ?? {});
                        setOpenUpdateGenericCfiWorkflowDialog(true);
                      }}
                      sx={{ fontSize: "25px", color: "#3b3f44" }}
                    />
                  </IconButton>
                  <IconButton>
                    <FileDownloadOutlinedIcon
                      sx={{ fontSize: "25px", color: "blue" }}
                    />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {openUpdateGenericCfiWorkflowDialog && (
        <UpdateGenericWorkflowDialog
          openUpdateGenericWorkflowDialog={openUpdateGenericCfiWorkflowDialog}
          handleUpdateGenericWorkflowDialog={handleUpdateGenericWorkflowDialog}
          workflowName={currentWorkflowName}
          currentWorkflowDetails={currentTxnWorkflowDetails}
        />
      )}
    </div>
  );
};

const CFI = ({ allocation, handleClose, isAuthorizedUser, isEditPolicy, viaHistoryLink = false }) => {
  const { history, formData, setFormData, submit, isApiInProgress, loadHistory } = useCfi(allocation);
  const [isDialogOpen, setIsDialogOpen] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const fixedDetails = useMemo(
    () => [
      {
        label: "Accounting Month",
        value: allocation?.accounting_monthyear
          ? acctMonthToLabel(allocation?.accounting_monthyear)
          : "",
      },
      {
        label: "Txn Date",
        value: customDateFormat(allocation?.bank_txn?.Payment_Receive_Date),
      },
      {
        label: "System",
        value: allocation?.system_id,
      },
      {
        label: "Bank A/c#",
        value: allocation?.bank_txn?.bank_details?.account_number,
      },
      {
        label: "Status",
        value: allocation?.allocation_status,
      },
      {
        label: "Receivable Amt",
        value: numberFormatter(allocation?.bank_txn?.Receivable_Amount ?? 0),
      },
    ],
    [allocation]
  );

  useEffect(() => {
    setShowHistory(!isAuthorizedUser || viaHistoryLink);
  }, [viaHistoryLink, isAuthorizedUser]);

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    handleClose("");
  }, [setIsDialogOpen]);

  const handleSubmitAssing = useCallback(
    async (e) => {
      e.preventDefault();
      const submitResponse = await submit();
      if (submitResponse && ("shouldShowHistory" in submitResponse) && submitResponse.shouldShowHistory) {
        setShowHistory(true);
        setFormData({
          aprover_name: allocation?.aprover_name || "",
          request_date: getTodaysDateInLocalTimezone()
        });
        loadHistory();
      }
    },
    [setIsDialogOpen, submit]
  );

  const handleInputChange = useCallback(
    (e) => {
      setFormData((data) => ({
        ...data,
        [e.target.name]: e.target.value,
      }));
    },
    [setFormData]
  );

  const handleAddShowHistory = () => {
    if (!isAuthorizedUser && showHistory) {
      toast.error("You are not assigned user!! Please re-assign and then try.", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
      return;
    }
    else {
      setShowHistory(!showHistory);
    }
  }

  return (
    <Dialog
      open={isDialogOpen}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        handleCloseDialog();
      }}
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "800px",
        },
      }}
    >
      <ToastContainer />
      <form onSubmit={handleSubmitAssing}>
        <DialogTitle
          style={{
            background: "#1672DE",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "70px",
            color: "white",
          }}
        >
          <Typography sx={{ fontSize: 20, color: "white" }}>CFI</Typography>
          <Typography
            sx={{ fontSize: 15, fontWeight: 750, marginLeft: "50px", color: "white" }}
          >
            Policy#: {allocation?.policy_id}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {showHistory ? (
            <History
              setShowHistory={setShowHistory}
              fixedDetails={fixedDetails}
              history={history}
            />
          ) : (
            <>
              <Grid container>
                {fixedDetails.map(({ label, value }, i) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={label}
                    paddingLeft={i % 2 ? 2 : 0}
                    paddingRight={i % 2 ? 0 : 2}
                    marginBottom={2}
                  >
                    <span>{label} </span>
                    <span style={{ fontWeight: "bold" }}>: {value}</span>
                  </Grid>
                ))}
              </Grid>

              <Grid container>
                <Grid item xs={12} md={6} paddingRight={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>Amount</p>

                  <Input
                    style={{ ...inputStyleObj }}
                    placeholder="Enter amount here"
                    name="cfi_amt"
                    value={formData.cfi_amt}
                    onChange={(e) => {
                      if (isNaN(e.target.value)) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.target.value = formData.cfi_amt || "";
                        return;
                      }
                      handleInputChange(e);
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6} paddingLeft={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>What Changed</p>

                  <TextareaAutosize
                    style={{
                      height: 50,
                      width: "100%",
                    }}
                    name="cfi_change"
                    value={formData.cfi_change}
                    onChange={handleInputChange}
                    placeholder="Write details here"
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6} paddingRight={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>
                    Impact to prior allocation
                  </p>

                  <Input
                    style={{ ...inputStyleObj }}
                    placeholder="Write details here"
                    name="cfi_impact"
                    value={formData.cfi_impact}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6} paddingLeft={2}>
                  <p style={{ fontSize: 13, color: "#17191B" }}>
                    Date Process in System
                  </p>

                  <Input
                    type="date"
                    style={{ ...inputStyleObj }}
                    name="process_date"
                    value={formData.process_date}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions style={{ justifyContent: "flex-end" }}>
          { !viaHistoryLink &&
          <Button
            onClick={() => handleAddShowHistory()}
            sx={{ 
              borderRadius: 100,
              backgroundColor: showHistory ? "#1672DE !important" : "transparent !important",
              color: showHistory ? "white !important" : "#1672DE !important",
              border: showHistory ? "none" : "1px solid #1672DE !important",
              textTransform: "none",
              "&:hover": {
                backgroundColor: showHistory ? "#1565c0 !important" : "rgba(22, 114, 222, 0.08) !important",
                color: showHistory ? "white !important" : "#1565c0 !important",
                border: showHistory ? "none" : "1px solid #1565c0 !important",
              }
            }}
          >
            {showHistory ? "Add" : "History"}
          </Button>
          }
          {!isEditPolicy && (<Button
            onClick={handleCloseDialog}
            sx={{ 
              borderRadius: 100,
              color: "#1672DE !important",
              border: "1px solid #1672DE !important",
              backgroundColor: "transparent !important",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "rgba(22, 114, 222, 0.08) !important",
                border: "1px solid #1565c0 !important",
                color: "#1565c0 !important",
              }
            }}
          >
            Close
          </Button>
          )}
          {!showHistory && (
            <Button
              sx={{ 
                borderRadius: 100,
                backgroundColor: "#1672DE !important",
                color: "white !important",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1565c0 !important",
                }
              }}
              type="submit"
              disabled={isApiInProgress}
            >
              Submit
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CFI;
